import { useState, useEffect, useCallback} from 'react';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
    setupFirestoreListeners,
    processStudentFile,
    generateExportData,
    generateCSV,
    createRoster,
    deleteRoster,
    updateRosterStudents
} from '../utils/instructorUtils';

export const useRosterManagement = (currentModel) => {
    const [rosters, setRosters] = useState([]);
    const [studentStatuses, setStudentStatuses] = useState({});
    const [activeUsers, setActiveUsers] = useState(new Set());
    const [userEmailToId, setUserEmailToId] = useState({});
    const [registeredUsers, setRegisteredUsers] = useState(new Set());
    const [fileInputKey, setFileInputKey] = useState(0);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const onRostersUpdate = useCallback((updatedRosters) => {
        setRosters(updatedRosters);
        setLastUpdate(new Date());
    }, []);

    const onUsersUpdate = useCallback((emailToId, registered) => {
        setUserEmailToId(emailToId);
        setRegisteredUsers(registered);
    }, []);

    const onActiveUsersUpdate = useCallback((users) => {
        setActiveUsers(users);
        setLastUpdate(new Date());
    }, []);

    const onModelsUpdate = useCallback((_, modelsByUser) => {
        if (!userEmailToId || Object.keys(userEmailToId).length === 0) return;

        const newStatuses = {};
        const idToEmail = Object.entries(userEmailToId).reduce((acc, [email, id]) => {
            acc[id] = email.toLowerCase();
            return acc;
        }, {});

        Object.values(userEmailToId).forEach(id => {
            const email = idToEmail[id];
            if (email) {
                newStatuses[email] = { isRegistered: true, hasModel: false };
            }
        });

        modelsByUser.forEach((models, userId) => {
            const email = idToEmail[userId];
            if (email && models.has(currentModel)) {
                newStatuses[email] = { ...newStatuses[email], hasModel: true };
            }
        });

        setStudentStatuses(newStatuses);
        setLastUpdate(new Date());
    }, [currentModel, userEmailToId]);

    useEffect(() => {
        const auth = getAuth();
        const db = getFirestore();
        const instructorId = auth.currentUser?.uid;

        if (!instructorId) {
            setIsLoading(false);
            return;
        }

        const cleanup = setupFirestoreListeners(db, instructorId, {
            onRostersUpdate,
            onUsersUpdate,
            onActiveUsersUpdate,
            onModelsUpdate
        });

        setIsLoading(false);

        return cleanup;
    }, [onRostersUpdate, onUsersUpdate, onActiveUsersUpdate, onModelsUpdate]);

    const handleFileUpload = async (rosterId, event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            const emails = processStudentFile(e.target.result);
            const db = getFirestore();
            await updateRosterStudents(db, rosterId, emails);
        };
        reader.readAsText(file);
        setFileInputKey(prev => prev + 1);
    };

    const handleAddRoster = async (rosterName) => {
        if (!rosterName.trim()) return;

        const auth = getAuth();
        const db = getFirestore();
        const instructorId = auth.currentUser?.uid;

        if (!instructorId) {
            console.error('No instructor ID found');
            return;
        }

        try {
            await createRoster(db, instructorId, rosterName);
            return true;
        } catch (error) {
            console.error('Error adding roster:', error);
            return false;
        }
    };

    const handleDeleteRoster = async (rosterId) => {
        const db = getFirestore();
        try {
            await deleteRoster(db, rosterId);
            return true;
        } catch (error) {
            console.error('Error deleting roster:', error);
            return false;
        }
    };

    const handleExportSummary = async (roster, selectedStudents = null) => {
        const db = getFirestore();
        try {
            const links = await generateExportData(db, roster, currentModel, userEmailToId, selectedStudents);
            const { csvContent, filename } = generateCSV(links, roster.name, currentModel);

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting summary:', error);
            throw error;
        }
    };

    return {
        rosters,
        studentStatuses,
        activeUsers,
        registeredUsers,
        fileInputKey,
        isLoading,
        lastUpdate,
        handleFileUpload,
        handleAddRoster,
        handleDeleteRoster,
        handleExportSummary,
        userEmailToId
    };
};